import type { WorkflowConfigInput } from '@automata/wfb-api';
import sortBy from 'lodash/sortBy';

import { NULL_LABWARE_TYPE } from './constants';
import { err, ok } from './utils';
import { DEFAULT_CUSTOM_LABWARE_DIMENSIONS } from '../../labware/labware';
import { LabwareDefByDefURI } from '../../labware/types';
import type { LabwareProperties } from '../../types';
import { LabwareOffsetDefinition } from '../../zodSchemas/labwareOffsetDefinition';
import * as LabwareErrors from '../shared/labwareErrors';
import type {
  MaybeWorkflowSerializationErrors,
  WorkflowSerializationError,
} from '../shared/types';

const CUSTOM_LABWARE_PREFIX = 'custom/';

const isCustomLabwareDefinitionURI = (
  labwareDefinitionURI: string | null
): labwareDefinitionURI is string =>
  typeof labwareDefinitionURI === 'string' &&
  labwareDefinitionURI.startsWith(CUSTOM_LABWARE_PREFIX);

export default function getJsonLabwareTypes(
  allDefinitions: LabwareDefByDefURI,
  labware: Record<string, LabwareProperties>,
  labwareOffsetDefinitions: Record<string, LabwareOffsetDefinition>
): MaybeWorkflowSerializationErrors<
  WorkflowConfigInput['workflow']['labware_types']
> {
  const errors: WorkflowSerializationError[] = [];
  const validLabwareTypes: WorkflowConfigInput['workflow']['labware_types'] =
    [];

  for (const l of sortBy(Object.values(labware), 'labwareDefinitionURI')) {
    const { id: labwareId, labwareDefinitionURI } = l;

    const definition =
      labwareDefinitionURI !== null
        ? allDefinitions[labwareDefinitionURI]
        : undefined;

    if (labwareDefinitionURI === null) {
      errors.push({
        errorType: 'labware',
        message: LabwareErrors.MISSING_LABWARE_DEFINITION_URI,
        labwareId,
      });
    } else if (
      definition === undefined &&
      !isCustomLabwareDefinitionURI(labwareDefinitionURI)
    ) {
      errors.push({
        errorType: 'labware',
        message: LabwareErrors.INVALID_LABWARE_DEFINITION_URI,
        labwareId,
        labwareDefinitionURI,
      });
    } else {
      const labwareOffsetDefinition = labwareOffsetDefinitions[labwareId] || {};
      const dimensions =
        definition?.dimensions ??
        (isCustomLabwareDefinitionURI(labwareDefinitionURI)
          ? DEFAULT_CUSTOM_LABWARE_DIMENSIONS
          : undefined);

      if (!validLabwareTypes.some(({ id }) => id === labwareId))
        validLabwareTypes.push({
          id: labwareId,
          dimensions: {
            height: dimensions?.zDimension,
            length: dimensions?.xDimension,
            width: dimensions?.yDimension,
          },
          ...labwareOffsetDefinition,
        });
    }
  }

  if (errors.length > 0) {
    return err(errors);
  }

  return ok(validLabwareTypes);
}

// TODO(AL, 10/07/2024): Loads of duplication here, consider refactoring
export function getJsonLabwareTypesForSave(
  allDefinitions: LabwareDefByDefURI,
  labware: Record<string, LabwareProperties>,
  labwareOffsetDefinitions: Record<string, LabwareOffsetDefinition>
): WorkflowConfigInput['workflow']['labware_types'] {
  const validLabwareTypes: WorkflowConfigInput['workflow']['labware_types'] =
    [];

  for (const l of sortBy(Object.values(labware), 'labwareDefinitionURI')) {
    const { labwareDefinitionURI } = l;
    const definition =
      labwareDefinitionURI !== null
        ? allDefinitions[labwareDefinitionURI]
        : undefined;

    const labwareTypeId = labwareDefinitionURI || NULL_LABWARE_TYPE;
    const labwareOffsetDefinition =
      labwareOffsetDefinitions[labwareTypeId] || {};
    const dimensions =
      definition?.dimensions ??
      (isCustomLabwareDefinitionURI(labwareDefinitionURI)
        ? DEFAULT_CUSTOM_LABWARE_DIMENSIONS
        : undefined);

    if (!validLabwareTypes.some(({ id }) => id === labwareTypeId)) {
      validLabwareTypes.push({
        id: labwareTypeId,
        dimensions: {
          height: dimensions?.zDimension,
          length: dimensions?.xDimension,
          width: dimensions?.yDimension,
        },
        ...labwareOffsetDefinition,
      });
    }
  }

  return validLabwareTypes;
}
